import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { Button } from "@/components/ui/button";
import { Toggle } from "@/components/ui/toggle";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, Quote, Link2, Image as ImageIcon, Undo, Redo, Code,
} from "lucide-react";
import { ArrowUp, ArrowDown, AlignLeft, AlignCenter, AlignRight } from "lucide-react";
import { useEffect, useState } from "react";

interface Props {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

const RichTextEditor = ({ value, onChange, placeholder }: Props) => {
  const [sourceMode, setSourceMode] = useState(false);
  const [sourceValue, setSourceValue] = useState(value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3, 4] } }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener", target: "_blank" } }),
      Image.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: { class: "rounded-lg my-4 max-w-full h-auto", draggable: "true" },
      }),
      Placeholder.configure({ placeholder: placeholder || "Commencez à écrire votre article…" }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "prose prose-sm sm:prose-base max-w-none min-h-[400px] focus:outline-none px-4 py-3",
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (sourceMode) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
  }, [value, editor, sourceMode]);

  if (!editor) return null;

  const headingValue = editor.isActive("heading", { level: 2 })
    ? "h2" : editor.isActive("heading", { level: 3 })
    ? "h3" : editor.isActive("heading", { level: 4 })
    ? "h4" : "p";

  const setBlock = (v: string) => {
    if (v === "p") editor.chain().focus().setParagraph().run();
    else editor.chain().focus().toggleHeading({ level: parseInt(v.slice(1)) as 2 | 3 | 4 }).run();
  };

  const addLink = () => {
    const previous = editor.getAttributes("link").href;
    const url = window.prompt("URL du lien", previous || "https://");
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const addImage = () => {
    const url = window.prompt("URL de l'image");
    if (!url) return;
    const alt = window.prompt("Texte alternatif (ALT)") || "";
    editor.chain().focus().setImage({ src: url, alt }).run();
  };

  // Find the top-level block node currently containing the selection. Returns { pos, node, depth } or null.
  const findBlockAtSelection = () => {
    if (!editor) return null;
    const { $from } = editor.state.selection;
    for (let d = $from.depth; d > 0; d--) {
      const node = $from.node(d);
      if (node.isBlock && node.type.name !== "doc") {
        return { pos: $from.before(d), node, depth: d };
      }
    }
    return null;
  };

  const moveBlock = (dir: -1 | 1) => {
    if (!editor) return;
    const block = findBlockAtSelection();
    if (!block) return;
    const { state } = editor;
    const $pos = state.doc.resolve(block.pos);
    const parent = $pos.parent;
    const index = $pos.index();
    const target = index + dir;
    if (target < 0 || target >= parent.childCount) return;
    const blockEnd = block.pos + block.node.nodeSize;
    const tr = state.tr;
    if (dir < 0) {
      const prev = parent.child(index - 1);
      const prevStart = block.pos - prev.nodeSize;
      tr.delete(block.pos, blockEnd);
      tr.insert(prevStart, block.node);
    } else {
      const next = parent.child(index + 1);
      const nextEnd = blockEnd + next.nodeSize;
      tr.delete(block.pos, blockEnd);
      tr.insert(nextEnd - block.node.nodeSize, block.node);
    }
    editor.view.dispatch(tr);
    editor.view.focus();
  };

  const setImageAlign = (align: "left" | "center" | "right") => {
    if (!editor) return;
    if (!editor.isActive("image")) return;
    const cls = align === "center"
      ? "rounded-lg my-4 max-w-full h-auto mx-auto block"
      : align === "right"
        ? "rounded-lg my-4 max-w-[50%] h-auto float-right ml-4"
        : "rounded-lg my-4 max-w-[50%] h-auto float-left mr-4";
    editor.chain().focus().updateAttributes("image", { class: cls }).run();
  };

  const imageActive = editor.isActive("image");

  return (
    <div className="border border-border rounded-lg bg-background">
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-border sticky top-0 z-10 bg-background/95 backdrop-blur">
        <Select value={headingValue} onValueChange={setBlock}>
          <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="p">Paragraphe</SelectItem>
            <SelectItem value="h2">Titre H2</SelectItem>
            <SelectItem value="h3">Titre H3</SelectItem>
            <SelectItem value="h4">Titre H4</SelectItem>
          </SelectContent>
        </Select>
        <span className="w-px h-6 bg-border mx-1" />
        <Toggle size="sm" pressed={editor.isActive("bold")} onPressedChange={() => editor.chain().focus().toggleBold().run()}><Bold className="h-4 w-4" /></Toggle>
        <Toggle size="sm" pressed={editor.isActive("italic")} onPressedChange={() => editor.chain().focus().toggleItalic().run()}><Italic className="h-4 w-4" /></Toggle>
        <Toggle size="sm" pressed={editor.isActive("underline")} onPressedChange={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon className="h-4 w-4" /></Toggle>
        <Toggle size="sm" pressed={editor.isActive("strike")} onPressedChange={() => editor.chain().focus().toggleStrike().run()}><Strikethrough className="h-4 w-4" /></Toggle>
        <span className="w-px h-6 bg-border mx-1" />
        <Toggle size="sm" pressed={editor.isActive("bulletList")} onPressedChange={() => editor.chain().focus().toggleBulletList().run()}><List className="h-4 w-4" /></Toggle>
        <Toggle size="sm" pressed={editor.isActive("orderedList")} onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></Toggle>
        <Toggle size="sm" pressed={editor.isActive("blockquote")} onPressedChange={() => editor.chain().focus().toggleBlockquote().run()}><Quote className="h-4 w-4" /></Toggle>
        <span className="w-px h-6 bg-border mx-1" />
        <Toggle size="sm" pressed={editor.isActive("link")} onPressedChange={addLink}><Link2 className="h-4 w-4" /></Toggle>
        <Button size="sm" variant="ghost" type="button" onClick={addImage} className="h-8 px-2"><ImageIcon className="h-4 w-4" /></Button>
        <span className="w-px h-6 bg-border mx-1" />
        <Button size="sm" variant="ghost" type="button" onClick={() => moveBlock(-1)} className="h-8 px-2" title="Remonter le bloc / l'image"><ArrowUp className="h-4 w-4" /></Button>
        <Button size="sm" variant="ghost" type="button" onClick={() => moveBlock(1)} className="h-8 px-2" title="Descendre le bloc / l'image"><ArrowDown className="h-4 w-4" /></Button>
        {imageActive && (
          <>
            <span className="w-px h-6 bg-border mx-1" />
            <Button size="sm" variant="ghost" type="button" onClick={() => setImageAlign("left")} className="h-8 px-2" title="Image à gauche"><AlignLeft className="h-4 w-4" /></Button>
            <Button size="sm" variant="ghost" type="button" onClick={() => setImageAlign("center")} className="h-8 px-2" title="Image centrée"><AlignCenter className="h-4 w-4" /></Button>
            <Button size="sm" variant="ghost" type="button" onClick={() => setImageAlign("right")} className="h-8 px-2" title="Image à droite"><AlignRight className="h-4 w-4" /></Button>
          </>
        )}
        <span className="w-px h-6 bg-border mx-1" />
        <Button size="sm" variant="ghost" type="button" onClick={() => editor.chain().focus().undo().run()} className="h-8 px-2"><Undo className="h-4 w-4" /></Button>
        <Button size="sm" variant="ghost" type="button" onClick={() => editor.chain().focus().redo().run()} className="h-8 px-2"><Redo className="h-4 w-4" /></Button>
        <div className="ml-auto">
          <Toggle size="sm" pressed={sourceMode} onPressedChange={(p) => {
            if (p) { setSourceValue(editor.getHTML()); setSourceMode(true); }
            else { onChange(sourceValue); editor.commands.setContent(sourceValue || ""); setSourceMode(false); }
          }}><Code className="h-4 w-4 mr-1" /> HTML</Toggle>
        </div>
      </div>
      {sourceMode ? (
        <textarea
          value={sourceValue}
          onChange={(e) => { setSourceValue(e.target.value); onChange(e.target.value); }}
          className="w-full min-h-[400px] p-4 font-mono text-xs bg-background outline-none resize-y"
        />
      ) : (
        <EditorContent editor={editor} />
      )}
    </div>
  );
};

export default RichTextEditor;